import { Fragment, type ReactNode } from "react";
import { PageTitle } from "@volca/ui/components/ui/page-components";
import { Stack } from "@volca/ui/components/ui/stack";

/**
 * A document review page's title block, in tiers: what the document is (its
 * title, then its facts), where it stands (each status beside what it is
 * about), and what can be done, together at the right. On a phone the
 * actions wrap below the title.
 */
export const ReviewHeaderLayout = ({
  title,
  facts,
  statuses,
  actions,
  children,
}: {
  title: ReactNode;
  /** Short facts (number, date, total), set off by dots. Falsy ones drop out. */
  facts: ReactNode[];
  /** `ReviewHeaderStatus` groups, ruled off from each other. Falsy ones drop out. */
  statuses?: ReactNode[];
  actions?: ReactNode;
  /** Notes under the block, e.g. why the document can't be pushed. */
  children?: ReactNode;
}) => {
  const shownFacts = facts.filter(Boolean);
  const shownStatuses = (statuses ?? []).filter(Boolean);

  return (
    <Stack className="w-full" align="start" gap={2}>
      <Stack
        direction="row"
        align="start"
        justify="between"
        gap={4}
        className="w-full flex-wrap"
      >
        <Stack align="start" gap={1} className="min-w-0">
          <PageTitle>{title}</PageTitle>
          <Stack
            direction="row"
            gap={2}
            align="center"
            className="flex-wrap text-sm text-subtle"
          >
            {shownFacts.map((fact, index) => (
              <Fragment key={index}>
                {index > 0 && <span aria-hidden>·</span>}
                {fact}
              </Fragment>
            ))}
          </Stack>
        </Stack>
        {actions && (
          <Stack direction="row" gap={2} align="center" className="flex-wrap">
            {actions}
          </Stack>
        )}
      </Stack>
      {shownStatuses.length > 0 && (
        <Stack direction="row" gap={4} align="center" className="flex-wrap">
          {shownStatuses.map((status, index) => (
            <Fragment key={index}>
              {/* Hidden on a phone, where the row wraps and the rule would
                  dangle at the end of a line. */}
              {index > 0 && (
                <span
                  className="hidden h-5 w-px bg-line sm:block"
                  aria-hidden
                />
              )}
              {status}
            </Fragment>
          ))}
        </Stack>
      )}
      {children}
    </Stack>
  );
};

/** One status in the header, with the things it qualifies beside it. */
export const ReviewHeaderStatus = ({ children }: { children: ReactNode }) => (
  <Stack
    direction="row"
    gap={2}
    align="center"
    className="flex-wrap text-sm text-subtle"
  >
    {children}
  </Stack>
);
